import {
  BAN_LIST_ENTRIES,
  type BanFormatId,
  type BanListEntry,
  formatsForBanEntry,
  formatBanFormatsLabel,
} from '../constants/banList';
import { foldCardNameForMatch } from './foldCardNameForMatch';
import { stripTrailingPrintSuffix } from './cardPrintSuffix';

/**
 * Fold + strip print suffixes for ban-list lookups.
 * Also drops a trailing "(Starter)" product label (not in the shared print-suffix list).
 */
export function banListNameKey(name: string): string {
  const stripped = stripTrailingPrintSuffix(name).replace(/\s*\(\s*Starter\s*\)\s*$/i, '');
  return foldCardNameForMatch(stripped);
}

/** YYYY-MM-DD in UTC — matches competitive effective-date announcements. */
export function banListDateKey(asOf: Date = new Date()): string {
  return asOf.toISOString().slice(0, 10);
}

function isEntryEffective(entry: BanListEntry, asOfKey: string): boolean {
  return entry.effectiveFrom <= asOfKey;
}

const ENTRIES_BY_KEY: ReadonlyMap<string, BanListEntry> = (() => {
  const map = new Map<string, BanListEntry>();
  for (const entry of BAN_LIST_ENTRIES) {
    map.set(banListNameKey(entry.name), entry);
  }
  return map;
})();

/**
 * Active ban entry for a card name as of `asOf` (default: now), or null.
 * Present even for format-scoped bans (e.g. 2v2-only). Works for both
 * RiftboundCard and DeckCardRef since both expose `name`.
 */
export function getCardBan(name: string, asOf: Date = new Date()): BanListEntry | null {
  const entry = ENTRIES_BY_KEY.get(banListNameKey(name));
  if (!entry) return null;
  return isEntryEffective(entry, banListDateKey(asOf)) ? entry : null;
}

/** Formats a card is banned in as of `asOf`, or an empty array if not banned. */
export function getCardBanFormats(name: string, asOf: Date = new Date()): readonly BanFormatId[] {
  const entry = getCardBan(name, asOf);
  return entry ? formatsForBanEntry(entry) : [];
}

/** Human-readable format list for UI / validation copy (e.g. "Standard / Constructed 2v2"). */
export function getCardBanFormatsLabel(name: string, asOf: Date = new Date()): string | null {
  const formats = getCardBanFormats(name, asOf);
  return formats.length > 0 ? formatBanFormatsLabel(formats) : null;
}

/**
 * True if the card is banned in Standard as of `asOf`.
 * Used by browse / detail stamps and deck validation (1v1 constructed context).
 * 2v2-only bans (e.g. Master Yi) return false here — use {@link isCardBannedInFormat}.
 */
export function isCardBanned(name: string, asOf: Date = new Date()): boolean {
  return isCardBannedInFormat(name, 'standard', asOf);
}

/** True if the card is banned in any tracked format as of `asOf`. */
export function isCardBannedInAnyFormat(name: string, asOf: Date = new Date()): boolean {
  return getCardBan(name, asOf) != null;
}

/**
 * True if the card is banned in a specific competitive format as of `asOf`.
 * Prefer this from the army / deck builder once format selection exists.
 */
export function isCardBannedInFormat(
  name: string,
  format: BanFormatId,
  asOf: Date = new Date(),
): boolean {
  return getCardBanFormats(name, asOf).includes(format);
}

/** Narrower check — banned non-battlefield cards in Standard (as of `asOf`). */
export function isBannedCard(name: string, asOf: Date = new Date()): boolean {
  const entry = getCardBan(name, asOf);
  return entry?.kind === 'card' && formatsForBanEntry(entry).includes('standard');
}

/** Narrower check — banned battlefields in Standard (as of `asOf`). */
export function isBannedBattlefield(name: string, asOf: Date = new Date()): boolean {
  const entry = getCardBan(name, asOf);
  return entry?.kind === 'battlefield' && formatsForBanEntry(entry).includes('standard');
}
